// resources/js/Pages/HallOfFame/components/RadarChart.jsx
import React, { useEffect, useRef, useState } from 'react';
import { radarPoints } from './radarPercentiles';

// Map radar labels → score field + rollup tooltip key
const RADAR_METRICS = {
    'Concept':          { field: 'conceptrating',          rollup: 'rollup_concept' },
    'Characters':       { field: 'charactersrating',       rollup: 'rollup_characters' },
    'Dialogue':         { field: 'dialoguerating',         rollup: 'rollup_dialogue' },
    'Emotional Impact': { field: 'emotional_impact',       rollup: 'rollup_emotionalimpact' },
    'Engagement':       { field: 'engagement_score',       rollup: 'rollup_engagement' },
    'Conflict':         { field: 'conflict_level',         rollup: 'rollup_conflictlevel' },
    'Originality':      { field: 'originality_score',      rollup: 'rollup_originality' },
    'Unpredictability':  { field: 'unpredictability_score', rollup: 'rollup_unpredictability' },
    'Pacing':           { field: 'pacing_score',           rollup: 'rollup_pacing' },
    'Plot':             { field: 'plotrating',             rollup: 'rollup_plot' }
};

export default function RadarChart({ script, allScripts }) {
    const chartRef = useRef(null);
    const [tooltipData, setTooltipData] = useState(null);

    // Store computed hover text in a ref so Plotly event handlers can access it
    const hoverTextRef = useRef([]);

    useEffect(() => {
        if (!script || !chartRef.current) return;

        if (typeof window.Plotly === 'undefined') {
            console.error('Plotly not loaded');
            return;
        }


        // Helper: extract executiveSummary from a rollup JSON field
        const getRollupSummary = (rollupKey) => {
            const raw = script.tooltips?.[rollupKey];
            if (!raw) return '';
            try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                const summary = parsed?.executiveSummary || '';
                // Truncate for hover readability
                return typeof summary === 'string' && summary.length > 300
                    ? summary.substring(0, 300) + '…'
                    : summary;
            } catch (e) {
                return '';
            }
        };

        const r = [];
        const theta = [];
        const tooltipItems = [];

        // Server percentiles only (same-reader gate, see radarPercentiles.js). No client fallback.
        const { available, points } = radarPoints(script.radarPercentiles, RADAR_METRICS, script.scores);
        if (!available) {
            window.Plotly.purge(chartRef.current);
            return;
        }

        points.forEach(({ label, field, percentile, rawScore }) => {
            r.push(percentile);
            theta.push(label);
            tooltipItems.push({
                label,
                percentile,
                rawScore: (typeof rawScore === 'number') ? rawScore.toFixed(1) : 'N/A',
                summary: getRollupSummary(RADAR_METRICS[label].rollup)
            });
        });

        // Store for event handler access
        hoverTextRef.current = tooltipItems;

        // Close the loop
        r.push(r[0]);
        theta.push(theta[0]);

        const data = [{
            type: 'scatterpolar',
            r: r,
            theta: theta,
            hoverinfo: 'none',  // Disable Plotly's built-in hover — we use custom tooltip
            fill: 'toself',
            name: 'Screenplay Percentiles',
            line: { color: '#23BAB1' },
            marker: { color: '#23BAB1', size: 8 }
        }];

        const layout = {
            polar: {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                radialaxis: {
                    visible: true,
                    range: [0, 100],
                    color: '#FFFFFF',
                    gridcolor: 'rgba(255, 255, 255, 0.3)'
                },
                angularaxis: {
                    tickfont: { size: 12, family: 'Poppins, sans-serif', color: '#FFFFFF' },
                    linecolor: 'rgba(255, 255, 255, 0.3)',
                    gridcolor: 'rgba(255, 255, 255, 0.3)'
                }
            },
            showlegend: false,
            margin: { l: 40, r: 40, b: 40, t: 40 },
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            autosize: true
        };

        const config = {
            responsive: true,
            displayModeBar: false,
            scrollZoom: false
        };

        window.Plotly.newPlot(chartRef.current, data, layout, config);

        // Custom hover: listen for Plotly hover/unhover events
        const chartEl = chartRef.current;

        const onHover = (eventData) => {
            if (!eventData?.points?.[0]) return;
            const pointIndex = eventData.points[0].pointIndex;
            // The last point is a duplicate (closes the loop), map it to index 0
            const idx = pointIndex >= tooltipItems.length ? 0 : pointIndex;
            setTooltipData(tooltipItems[idx]);
        };

        const onUnhover = () => {
            setTooltipData(null);
        };

        chartEl.on('plotly_hover', onHover);
        chartEl.on('plotly_unhover', onUnhover);

        // Handle resize
        const resizeObserver = new ResizeObserver(() => {
            if (chartRef.current && chartRef.current.offsetWidth > 0) {
                const newWidth = chartRef.current.offsetWidth;
                window.Plotly.relayout(chartRef.current, {
                    width: newWidth,
                    height: newWidth
                });
            }
        });

        resizeObserver.observe(chartRef.current);

        return () => {
            if (chartRef.current) {
                chartRef.current.removeAllListeners?.('plotly_hover');
                chartRef.current.removeAllListeners?.('plotly_unhover');
                window.Plotly.purge(chartRef.current);
            }
            resizeObserver.disconnect();
        };
    }, [script, allScripts]);

    const { available, missing } = radarPoints(script?.radarPercentiles, RADAR_METRICS, script?.scores);
    if (!available) {
        return (
            <div className="text-white-50 small p-2">
                Scene percentiles aren&rsquo;t shown for this script: its scene scores can&rsquo;t yet be
                compared like-for-like with the rest of the vault.
            </div>
        );
    }

    return (
        <div className="position-relative" style={{ width: '100%', aspectRatio: '1 / 1' }}>
            {missing.length > 0 && (
                <div className="text-white-50 small">Not compared: {missing.join(', ')}</div>
            )}
            <div
                ref={chartRef}
                className="js-plotly-plot"
                style={{ width: '100%', height: '100%' }}
            ></div>
            {tooltipData && (
                <div className="radar-tooltip" style={{ display: 'block' }}>
                    <strong>{tooltipData.label}</strong>
                    <div className="intensity">
                        Percentile: {tooltipData.percentile}% (Score: {tooltipData.rawScore})
                    </div>
                    {tooltipData.summary && (
                        <div className="description">
                            {tooltipData.summary}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
